<template>
  <div v-if="showModule" class="news-module">
    <div class="news-content" @wheel.stop>
      <div v-if="loading" class="news-state">
        <div class="news-list news-list-loading" aria-hidden="true">
          <div v-for="index in 5" :key="index" class="news-item news-item-skeleton">
            <Skeleton shape="circle" size="3.25rem" class="news-skeleton-avatar" />
            <div class="news-skeleton-copy">
              <Skeleton width="11rem" height="0.95rem" borderRadius="999px" />
            </div>
            <Skeleton width="1.25rem" height="1.25rem" borderRadius="999px" />
          </div>
        </div>
      </div>

      <div v-else-if="error" class="news-state">
        <EmptyState
          :title="t('wkt.client.chat.error.title')"
          :description="t('wkt.client.chat.error.description')"
        />
      </div>

      <div v-else-if="channels.length === 0" class="news-state">
        <EmptyState
          :title="t('wkt.client.chat.empty-state.title')"
          :description="t('wkt.client.chat.empty-state.description')"
        />
      </div>

      <div v-else class="news-list">
        <button
          v-for="channel in channels"
          :key="channel.id"
          type="button"
          class="news-item"
          @click="openChannel(channel)"
        >
          <div class="news-avatar">
            <img
              v-if="shouldShowAvatar(channel)"
              :src="getAvatarUrl(channel.id)"
              :alt="getChannelName(channel)"
              @error="markAvatarFailed(channel.id)"
            />
            <span v-else>{{ getChannelInitial(channel) }}</span>
          </div>

          <div class="news-copy">
            <span class="news-name">{{ getChannelName(channel) }}</span>
          </div>

          <span v-if="(channel.unread ?? 0) > 0" class="news-badge">
            {{ channel.unread }}
          </span>

          <i class="pi pi-angle-right news-chevron" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import type { NewsModulePayload, RocketChannelDTO } from "@wkt/api/newsDTO";
import EmptyState from "@wkt/components/common/EmptyState.vue";

const { t } = useI18n();

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const channels = ref<RocketChannelDTO[]>([]);
const avatarBaseUrl = ref<string | null>(null);
const failedAvatars = ref<Record<string, boolean>>({});

function isRocketChannelPayload(payload: unknown): payload is RocketChannelDTO[] {
  return Array.isArray(payload) && payload.every((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }

    const candidate = item as Partial<RocketChannelDTO>;
    return typeof candidate.id === "string";
  });
}

function isNewsModulePayload(payload: unknown): payload is NewsModulePayload {
  return Boolean(payload) && typeof payload === "object" && !Array.isArray(payload);
}

function loadAvatarBaseUrl(): void {
  const configuration = gwtApi.getConfigurations().find(
    (item) => item.key === "client.rocket.avatar.base.url"
  );

  avatarBaseUrl.value = configuration?.value?.trim() || null;
}

function normalizeChannels(payload: unknown): RocketChannelDTO[] | null {
  if (!isRocketChannelPayload(payload)) {
    return null;
  }

  return payload.map((item) => ({
    id: item.id,
    name: item.name?.trim() || item.id,
    unread: typeof item.unread === "number" ? item.unread : 0,
  }));
}

function normalizePayload(payload: unknown): NewsModulePayload | null {
  if (payload == null) {
    return {
      loading: true,
      error: false,
      channels: [],
    };
  }

  if (isRocketChannelPayload(payload)) {
    return {
      loading: false,
      error: false,
      channels: normalizeChannels(payload) ?? [],
    };
  }

  if (!isNewsModulePayload(payload)) {
    return null;
  }

  return {
    loading: Boolean(payload.loading),
    error: Boolean(payload.error),
    channels: Array.isArray(payload.channels) ? normalizeChannels(payload.channels) ?? [] : [],
  };
}

function getChannelName(channel: RocketChannelDTO): string {
  return channel.name?.trim() || channel.id;
}

function getChannelInitial(channel: RocketChannelDTO): string {
  return getChannelName(channel).trim().charAt(0).toUpperCase() || "#";
}

function getAvatarUrl(channelId: string): string {
  return `${avatarBaseUrl.value ?? ""}${channelId}`;
}

function shouldShowAvatar(channel: RocketChannelDTO): boolean {
  return Boolean(avatarBaseUrl.value) && !failedAvatars.value[channel.id];
}

function markAvatarFailed(channelId: string): void {
  failedAvatars.value = {
    ...failedAvatars.value,
    [channelId]: true,
  };
}

function buildChannelHash(channel: RocketChannelDTO): string {
  return `#chat;g=${encodeURIComponent(getChannelName(channel))};r=${encodeURIComponent(channel.id)}`;
}

function openChannel(channel: RocketChannelDTO): void {
  window.location.hash = buildChannelHash(channel);
}

function applyPayload(payload?: unknown): void {
  const normalizedPayload = normalizePayload(payload);

  loadAvatarBaseUrl();
  failedAvatars.value = {};

  if (normalizedPayload === null) {
  channels.value = [];
  error.value = true;
  loading.value = false;
  return;
  }

  channels.value = normalizedPayload.channels ?? [];
  error.value = Boolean(normalizedPayload.error);
  loading.value = Boolean(normalizedPayload.loading);
}

function onReveal(payload?: unknown): void {
  showModule.value = true;
  applyPayload(payload);
}

function refreshModule(): void {
  showModule.value = true;
  loading.value = true;
  error.value = false;
}

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.news-module {
  min-height: 100%;
}

.news-content {
  min-height: 100%;
  padding: 16px 0 24px;
}

.news-state {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 14px;
}

.news-list-loading {
  width: 100%;
}

.news-item {
  width: 100%;
  border: 0;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  text-align: left;
}

.news-item-skeleton {
  cursor: default;
}

.news-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.12);
}

.news-item-skeleton:hover {
  transform: none;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
}

.news-avatar {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: rgba(var(--primary-rgb, 24, 94, 169), 0.12);
  color: var(--primary-color, #185ea9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
}

.news-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-copy {
  min-width: 0;
  flex: 1;
}

.news-skeleton-copy {
  min-width: 0;
  flex: 1;
}

.news-name {
  display: block;
  color: #1f2937;
  font-size: 0.98rem;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
}

.news-badge {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-color, #185ea9);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.news-chevron {
  color: #94a3b8;
  font-size: 0.95rem;
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .news-content {
    padding: 12px 0 18px;
  }

  .news-list {
    padding: 0 10px;
    gap: 8px;
  }

  .news-item {
    padding: 12px 14px;
    border-radius: 16px;
  }
}
</style>
